/**
 * Encryption of the on-device queue (PROJECT_SPEC.md §8, OFF-002; 21 CFR 11.10(d) limiting access, 11.30).
 *
 * The queue holds unsynced GMP records, so a lost tablet must not hand them over. Each record is sealed with
 * AES-256-GCM under a key derived from the operator's device PIN with PBKDF2-HMAC-SHA256. The PIN and the key
 * never leave the device and are never stored: only a salt and an encrypted check value are, so a wrong PIN is
 * detected without anything on disk that would let one be guessed offline faster than the KDF allows.
 *
 * Why PBKDF2 and not Argon2id (§8.5 names it): WebCrypto has no Argon2, and shipping a WASM implementation into
 * the GxP path would add a supplier for no gain a 600k-iteration PBKDF2 and a 5-try lockout do not already give.
 * ponytail: swap to an Argon2id WASM build if the assessment asks for it; only deriveKey changes.
 */

/** OWASP 2023 guidance for PBKDF2-HMAC-SHA256. */
export const KDF_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12; // the GCM standard; a fresh random one per record
const CHECK_PLAINTEXT = "corpex-ebr-offline-v1";

export interface Sealed {
  readonly iv: string;
  readonly ct: string;
}

const b64 = (bytes: ArrayBuffer | Uint8Array): string => {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s);
};
const unb64 = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export const newSalt = (): string => b64(crypto.getRandomValues(new Uint8Array(SALT_BYTES)));

/** Non-extractable: even script running in the page cannot read the key out, only use it. */
export async function deriveKey(pin: string, salt: string, iterations = KDF_ITERATIONS): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: unb64(salt), iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function seal(key: CryptoKey, value: unknown): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(JSON.stringify(value)),
  );
  return { iv: b64(iv), ct: b64(ct) };
}

/** Throws if the key is wrong or the record was altered: GCM authenticates as well as encrypts. */
export async function open<T>(key: CryptoKey, sealed: Sealed): Promise<T> {
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(sealed.iv) }, key, unb64(sealed.ct));
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

export const makeCheck = (key: CryptoKey): Promise<Sealed> => seal(key, CHECK_PLAINTEXT);

export async function checkMatches(key: CryptoKey, check: Sealed): Promise<boolean> {
  try {
    return (await open<string>(key, check)) === CHECK_PLAINTEXT;
  } catch {
    return false;
  }
}
